import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { get, post } from '../../helpers/apiHelper';
import publicPageCustomizationService from '../../helpers/publicPageCustomizationHelper';
import { FaExclamationTriangle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import FindTicketPageContent from '../../components/public/FindTicketPageContent';

const FindTicketPage = () => {
    const { marcaId } = useParams();
    const navigate = useNavigate();
    const [marca, setMarca] = useState(null);
    const [loading, setLoading] = useState(true);

    // Estados para personalización
    const [customization, setCustomization] = useState(null);
    const [texts, setTexts] = useState({
        pageTitle: 'Buscar mi Tarjeta de Puntos',
        pageDescription: 'Ingresa tu correo electrónico para encontrar tu tarjeta digital de fidelización',
        emailLabel: 'Correo Electrónico',
        emailPlaceholder: 'tu@email.com',
        emailHelpText: 'El correo debe ser el mismo que usaste al registrarte en {{brandName}}',
        searchButtonText: 'Buscar mi Tarjeta',
        searchingButtonText: 'Buscando...',
        ticketNotFoundTitle: 'No tienes tarjeta en este comercio',
        ticketNotFoundMessage: 'No encontramos una tarjeta de puntos asociada a este correo electrónico en {{brandName}}.',
        joinClubQuestion: '¿Quieres unirte al club?',
        joinClubButtonText: 'Sí, unirme al club',
        cancelButtonText: 'Cancelar',
        helpSectionTitle: '¿Necesitas ayuda?',
        helpItem1: 'Asegúrate de usar el mismo correo electrónico con el que te registraste',
        helpItem2: 'El correo debe estar registrado en {{brandName}}',
        helpItem3: 'Si no encuentras tu tarjeta, puedes unirte al club de clientes'
    });
    const [styles, setStyles] = useState({});
    const [layout, setLayout] = useState('centered');
    const [imagenFondo, setImagenFondo] = useState(null);
    const [layoutOptions, setLayoutOptions] = useState({
        showLogo: true,
        showSlogan: true,
        showBrandName: true,
        showOverlay: true,
        showQrIcon: true,
        logoPosition: 'center',
        headerStyle: 'full',
        cardStyle: 'elevated'
    });

    // Función para procesar variables en textos
    const processVariables = (text, variables) => {
        if (!text) return '';
        let processed = text;
        Object.keys(variables).forEach(key => {
            const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
            processed = processed.replace(regex, variables[key] || '');
        });
        return processed;
    };

    useEffect(() => {
        async function fetchData() {
            try {
                // Cargar marca y personalización en paralelo
                const [marcaData, customizationResponse] = await Promise.all([
                    get(`marcas/${marcaId}`).catch(() => null),
                    publicPageCustomizationService.getCustomization(marcaId, 'findTicket').catch((err) => {
                        // Si es 404, es normal (no hay personalización), retornar null
                        if (err.status === 404) {
                            return null;
                        }
                        // Para otros errores, loguear pero continuar
                        console.warn('Error obteniendo personalización:', err);
                        return null;
                    })
                ]);

                if (!marcaData) {
                    setLoading(false);
                    return;
                }

                setMarca(marcaData);

                // Normalizar datos de personalización (puede venir como objeto Mongoose)
                let customizationData = null;
                if (customizationResponse) {
                    // Si viene como objeto Mongoose, extraer _doc
                    if (customizationResponse._doc) {
                        customizationData = customizationResponse._doc;
                    } else if (customizationResponse.pageName) {
                        // Si viene directamente como página
                        customizationData = customizationResponse;
                    } else {
                        // Si viene envuelto en otro formato
                        customizationData = customizationResponse;
                    }
                }


                // Cargar tipografía desde personalización o marca (solo si es una fuente de Google Fonts)
                const fontFamilyToLoad = customizationData?.styles?.fontFamily || marcaData.tipografia;
                if (fontFamilyToLoad) {
                    // Fuentes del sistema que NO deben cargarse desde Google Fonts
                    const systemFonts = ['Arial', 'Helvetica', 'Verdana', 'Tahoma', 'Georgia', 'Times New Roman', 'sans-serif', 'serif', 'monospace'];
                    const fontName = fontFamilyToLoad.split(',')[0].trim().replace(/['"]/g, '');

                    if (!systemFonts.includes(fontName)) {
                        const link = document.createElement('link');
                        link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(' ', '+')}&display=swap`;
                        link.rel = 'stylesheet';
                        document.head.appendChild(link);
                    }
                }

                // Procesar personalización
                // isActive debe ser true por defecto si no está definido, o usar el valor explícito
                const isActive = customizationData?.isActive !== undefined ? customizationData.isActive : true;

                // LOG: Datos de la página real FindTicket
                console.log('[FindTicketPage] ===== DATOS DE LA PÁGINA REAL =====');
                console.log('[FindTicketPage] layout:', customizationData?.layout);
                console.log('[FindTicketPage] layoutOptions:', customizationData?.layoutOptions);
                console.log('[FindTicketPage] texts (raw):', customizationData?.texts);
                console.log('[FindTicketPage] styles:', customizationData?.styles);
                console.log('[FindTicketPage] marca:', {
                    nombre: marcaData?.nombre,
                    eslogan: marcaData?.eslogan,
                    logotipo: marcaData?.logotipo ? (marcaData.logotipo.cdnUrl || marcaData.logotipo.url) : 'no existe',
                    imagenFondo: marcaData?.imagenFondo ? (marcaData.imagenFondo.cdnUrl || marcaData.imagenFondo.url) : 'no existe',
                    tipografia: marcaData?.tipografia
                });
                console.log('[FindTicketPage] isActive:', isActive);
                console.log('[FindTicketPage] ====================================');

                if (customizationData && isActive) {
                    setCustomization(customizationData);

                    // Aplicar textos personalizados (con procesamiento de variables)
                    const variables = {
                        brandName: marcaData.nombre || ''
                    };

                    const processedTexts = {};
                    const textsToProcess = customizationData.texts || {};
                    Object.keys(textsToProcess).forEach(key => {
                        processedTexts[key] = processVariables(textsToProcess[key], variables);
                    });

                    setTexts(prev => ({ ...prev, ...processedTexts }));

                    // Aplicar estilos personalizados
                    if (customizationData.styles) {
                        // Asegurar que todos los estilos se apliquen, incluso si algunos están vacíos
                        setStyles(customizationData.styles);
                    }

                    // Aplicar layout y opciones
                    if (customizationData.layout) {
                        setLayout(customizationData.layout);
                    }
                    if (customizationData.layoutOptions) {
                        // Filtrar solo las opciones válidas para findTicket
                        const validOptions = {
                            showLogo: customizationData.layoutOptions.showLogo,
                            showSlogan: customizationData.layoutOptions.showSlogan,
                            showBrandName: customizationData.layoutOptions.showBrandName,
                            showOverlay: customizationData.layoutOptions.showOverlay,
                            showQrIcon: customizationData.layoutOptions.showQrIcon,
                            logoPosition: customizationData.layoutOptions.logoPosition,
                            headerStyle: customizationData.layoutOptions.headerStyle,
                            cardStyle: customizationData.layoutOptions.cardStyle
                        };
                        // Eliminar valores undefined
                        Object.keys(validOptions).forEach(key => {
                            if (validOptions[key] === undefined) {
                                delete validOptions[key];
                            }
                        });
                        setLayoutOptions(prev => ({ ...prev, ...validOptions }));
                    }

                    // Aplicar imagen de fondo si existe
                    if (customizationData.imagenFondo) {
                        setImagenFondo(customizationData.imagenFondo);
                    } else {
                        setImagenFondo(null);
                    }
                } else {
                    // Usar valores por defecto, pero procesar variables en textos por defecto
                    const variables = {
                        brandName: marcaData.nombre || ''
                    };
                    const defaultTexts = { ...texts };
                    Object.keys(defaultTexts).forEach(key => {
                        defaultTexts[key] = processVariables(defaultTexts[key], variables);
                    });
                    setTexts(defaultTexts);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        }

        if (marcaId) {
            fetchData();
        }
    }, [marcaId]);

    const handleSearch = async (email) => {
        if (!email.trim()) {
            Swal.fire({
                title: 'Email requerido',
                text: 'Por favor, ingresa tu correo electrónico',
                icon: 'warning',
                confirmButtonText: 'Aceptar'
            });
            throw new Error('Email requerido');
        }

        // Validar formato de email básico
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            Swal.fire({
                title: 'Email inválido',
                text: 'Por favor, ingresa un correo electrónico válido',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            throw new Error('Email inválido');
        }

        const response = await post('tickets/find-by-email', {
            email: email.trim().toLowerCase(),
            marcaId: marcaId
        });

        if (response.success && response.link) {
            // Redirigir directamente a la tarjeta
            window.location.href = response.link;
        } else {
            throw new Error('Ticket no encontrado');
        }
    };

    const handleJoinClub = () => {
        // Obtener la primera tienda de la marca para redirigir al registro
        if (marca && marca.tiendas && marca.tiendas.length > 0) {
            const firstTiendaId = marca.tiendas[0];
            navigate(`/qr-register/${firstTiendaId}`);
        } else {
            Swal.fire({
                title: 'Error',
                text: 'No hay tiendas disponibles para el registro',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-pulse text-gray-600 flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-t-blue-500 border-gray-200 rounded-full animate-spin mb-4"></div>
                    <p className="text-lg">Cargando...</p>
                </div>
            </div>
        );
    }

    if (!marca) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-600">
                    <FaExclamationTriangle className="text-4xl mx-auto mb-4 text-red-500" />
                    <p className="text-xl">Marca no encontrada</p>
                    <p className="text-sm mt-2">La marca especificada no existe o no está disponible</p>
                </div>
            </div>
        );
    }

    // LOG: Datos antes de renderizar
    console.log('[FindTicketPage] ===== DATOS ANTES DE RENDERIZAR =====');
    console.log('[FindTicketPage] layout:', layout);
    console.log('[FindTicketPage] layoutOptions:', layoutOptions);
    console.log('[FindTicketPage] styles:', styles);
    console.log('[FindTicketPage] texts:', texts);
    console.log('[FindTicketPage] marca:', {
        nombre: marca?.nombre,
        eslogan: marca?.eslogan,
        logotipo: marca?.logotipo ? (marca.logotipo.cdnUrl || marca.logotipo.url) : 'no existe',
        imagenFondo: marca?.imagenFondo ? (marca.imagenFondo.cdnUrl || marca.imagenFondo.url) : 'no existe'
    });
    console.log('[FindTicketPage] =====================================');

    return (
        <FindTicketPageContent
            marca={marca}
            texts={texts}
            styles={styles}
            layout={layout}
            layoutOptions={layoutOptions}
            imagenFondo={imagenFondo}
            onSearch={handleSearch}
            onJoinClub={handleJoinClub}
            isPreview={false}
        />
    );
};

export default FindTicketPage;

